import React, { useCallback, useMemo, useLayoutEffect, useState, useEffect } from 'react';
import { ExpandableCalendar, AgendaList, CalendarProvider, WeekCalendar, Calendar, DateData } from 'react-native-calendars';
import { getTheme, themeColor } from '@/components/calendar/theme';
import { DayComponentProps, getMarkedDates, mapJobDateCrewListForFrontend } from '@/components/calendar/types';
import { AvailabilityCard } from '@/components/calendar/availability-card';
import { availabilitiesOnDay } from '@/components/calendar/availability-colors';
import { addDaysToDayKey, londonDayKey } from '@/lib/availability-dates';
import { CrewAvailability } from '@/components/types/CrewAvailability';
import JobDateItem from '@/components/calendar/job-date-item';
import { trpc } from '@/trpc/react';
import { useCrew } from '@/context/CrewProvider';
import { DrawerToggleButton } from '@react-navigation/drawer';
import { useNavigation } from '@react-navigation/native';
import { ActivityIndicator, View, Pressable, RefreshControl, Text, Platform, StyleSheet, TouchableOpacity } from 'react-native';
import { Button } from '@/components/ui';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { formatDate } from 'shared-utils';
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus';
import { AvailabilityPastAlert } from '@/components/calendar/availability-past-alert';
import { Positions } from 'react-native-calendars/src/expandableCalendar';
import colors from '@/constants/Colors';
import { startOfMonth, endOfMonth, startOfDay, endOfDay, subDays, isSaturday, isSunday, format, parse, subMonths, getYear, getMonth, addHours } from 'date-fns';
import { useSettings } from '@/hooks/useSettings';
import { mapJobTimesheetToFrontend } from '@/components/timesheets/types';
import { cn } from "@/lib/utils";
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  weekView?: boolean;
}

const renderSectionHeader = (info: any) => {
  if (!info || !info.section) return null;

  const title = info.section.title || info.section.day || info.section.toString();

  if (!title) return null;

  try {
    const dateObj = new Date(title);
    if (!isNaN(dateObj.getTime())) {
      const formattedDate = formatDate(dateObj, 'dd MMM yyyy').toUpperCase();
      return (
        <View className="py-3 px-6" style={{ backgroundColor: '#f5f5f5' }}>
          <Text className="text-sm font-semibold text-gray-500 tracking-wide">{formattedDate}</Text>
        </View>
      );
    }
  } catch (e) {
    return (
      <View className="py-3 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <Text className="text-sm font-semibold text-gray-500 tracking-wide">{title}</Text>
      </View>
    );
  }

  return (
    <View className="py-3 px-6" style={{ backgroundColor: '#f5f5f5' }}>
      <Text className="text-sm font-semibold text-gray-500 tracking-wide">{title}</Text>
    </View>
  );
};

const getPaymentDates = (month: number, year: number, biweekly: boolean = false): Date[] => {
  const paymentDays = [5, ...(biweekly ? [20] : [])];

  return paymentDays.map(day => {
    let date = new Date(year, month - 1, day);

    if (isSaturday(date)) date = subDays(date, 1);
    else if (isSunday(date)) date = subDays(date, 2);

    return date;
  });
};

const getPrevPaymentDate = (paydayString: string, paydayDates: Date[]) => {
  const paydayIndex = paydayDates.findIndex(day => format(day, 'yyyy-MM-dd') === paydayString)

  if (paydayIndex === -1)
    return null

  if (paydayIndex === 1)
    return paydayDates[0]

  const prevMonthDate = subMonths(parse(paydayString, 'yyyy-MM-dd', new Date()), 1)
  const prevPaydates = getPaymentDates(prevMonthDate.getMonth() + 1, prevMonthDate.getFullYear(), true);

  return paydayDates.length === 1 ? prevPaydates[0] : prevPaydates[1]
}

const ExpandableCalendarScreen = (props: Props) => {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { currentCrewMember } = useCrew();
  const navigation = useNavigation();
  const { weekView } = props;

  // Initialize with today's date
  const today = londonDayKey(new Date());

  // A day can be covered by several periods at once; keeping only one hid the rest.
  const [selectedDate, setSelectedDate] = useState<string | null>(today);
  const [openEmployeeAvailability, setOpenEmployeeAvailability] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showCalendar, setShowCalendar] = useState(true); // New state for calendar visibility
  const [paydayDates, setPaydayDates] = useState<Date[]>([]);

  const extraHoursRate = useSettings('extra-hours');
  const plInsuranceRate = useSettings('pl-insurance-rate');

  // Common query options to prevent excessive refetching
  const queryOptions = {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  };

  const theme = useMemo(() => getTheme(), []);
  const todayBtnTheme = useMemo(() => ({
    todayButtonTextColor: themeColor
  }), []);

  useLayoutEffect(() => {
    navigation.setOptions({
      drawerLabel: 'Calendar',
      title: 'Calendar',
      headerStyle: {
        backgroundColor: colors.primary, // Black header
      },
      headerTintColor: colors.accent, // Yellow text
      headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />,
      headerRight: () => (
        <View className="flex-row items-center">
          <Button className="bg-transparent -mr-2" onPress={() => setShowCalendar(!showCalendar)}>
            <Ionicons
              name={showCalendar ? "calendar" : "calendar-outline"}
              size={24}
              color={colors.accent}
            />
          </Button>
          <Button className="bg-transparent -mr-4" onPress={() => router.navigate(`/(app)/home/add`)}>
            <Ionicons name="add" size={24} color={colors.accent} />
          </Button>
        </View>
      ),
    });
  }, [navigation, showCalendar]);

  const { data: jobDatesData, isLoading, refetch: jobDatesRefetch } = trpc.jobDateListRouter.listForCrew.useQuery(
    { id: currentCrewMember!.id, states: [2, 3] },
    {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 30,
      retry: 3,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const { data: crewAvailability, isLoading: crewAvailabilityLoading, refetch: crewAvailabilityRefetch } = trpc.crew.getCrewAvailability.useQuery(
    currentCrewMember!.id,
    {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 30,
      retry: 3,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const { data: feedbackData, isLoading: feedbackLoading, refetch: feedbackRefetch } = trpc.jobDateListRouter.listFeedbackForDates.useQuery(
    jobDatesData?.data.map(jobDate => jobDate.date_id).filter((id): id is number => id !== null) || [],
    {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 30,
      retry: 3,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      enabled: !!jobDatesData
    }
  );

  const [{ data: timesheetData, refetch: timesheetRefetch },
    { data: additionsData, refetch: additionsRefetch },
    { data: deductionsData, refetch: deductionsRefetch },
    { data: strikesData, refetch: strikesRefetch },
    { data: plInsuranceSkillData, refetch: plInsuranceSkillRefetch }
  ] = trpc.useQueries((t) => [
    t.jobTimesheet.listForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfMonth(selectedDate ? new Date(selectedDate) : new Date()),
      toDate: endOfMonth(selectedDate ? new Date(selectedDate) : new Date())
    }, queryOptions),
    t.jobTimesheet.listAdditionsForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfMonth(selectedDate ? new Date(selectedDate) : new Date()),
      toDate: endOfMonth(selectedDate ? new Date(selectedDate) : new Date())
    }, queryOptions),
    t.jobTimesheet.listDeductionsForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfMonth(selectedDate ? new Date(selectedDate) : new Date()),
      toDate: endOfMonth(selectedDate ? new Date(selectedDate) : new Date())
    }, queryOptions),
    t.jobTimesheet.listStrikesForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfMonth(selectedDate ? new Date(selectedDate) : new Date()),
      toDate: endOfMonth(selectedDate ? new Date(selectedDate) : new Date())
    }, queryOptions),
    t.jobTimesheet.getPLInsuranceSkillForCrew(currentCrewMember!.id, queryOptions)
  ]);

  const paydaySelected = paydayDates.some(pd => format(pd, 'yyyy-MM-dd') === selectedDate)
  const paydayStartDate = paydaySelected ? getPrevPaymentDate(selectedDate!, paydayDates) : null
  const paydayEndDate = paydaySelected ? subDays(parse(selectedDate!, 'yyyy-MM-dd', new Date()), 1) : null

  const [{ data: paydayTimesheetData, refetch: paydayTimesheetRefetch },
    { data: paydayAdditionsData, refetch: paydayAdditionsRefetch },
    { data: paydayDeductionsData, refetch: paydayDeductionsRefetch },
    { data: paydayStrikesData, refetch: paydayStrikesRefetch },
    { data: paydayPlInsuranceSkillData, refetch: paydayPlInsuranceSkillRefetch }
  ] = trpc.useQueries((t) => [
    t.jobTimesheet.listForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfDay(paydayStartDate!),
      toDate: endOfDay(paydayEndDate!)
    }, { ...queryOptions, enabled: paydaySelected }),
    t.jobTimesheet.listAdditionsForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfDay(paydayStartDate!),
      toDate: endOfDay(paydayEndDate!)
    }, { ...queryOptions, enabled: paydaySelected }),
    t.jobTimesheet.listDeductionsForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfDay(paydayStartDate!),
      toDate: endOfDay(paydayEndDate!)
    }, { ...queryOptions, enabled: paydaySelected }),
    t.jobTimesheet.listStrikesForCrew({
      id: currentCrewMember!.id,
      fromDate: startOfDay(paydayStartDate!),
      toDate: endOfDay(paydayEndDate!)
    }, { ...queryOptions, enabled: paydaySelected }),
    t.jobTimesheet.getPLInsuranceSkillForCrew(currentCrewMember!.id, { ...queryOptions, enabled: paydaySelected })
  ]);

  const { data: contractTypeHistory } = trpc.crew.listContractTypeHistory.useQuery(Number(currentCrewMember?.id));

  const mappedTimesheet = timesheetData && additionsData && deductionsData && strikesData && plInsuranceSkillData ? mapJobTimesheetToFrontend(timesheetData, extraHoursRate, additionsData, deductionsData, strikesData, plInsuranceRate, plInsuranceSkillData?.data) : null;
  const mappedPaydayTimesheet = paydayTimesheetData && paydayAdditionsData && paydayDeductionsData && paydayStrikesData && paydayPlInsuranceSkillData ? mapJobTimesheetToFrontend(paydayTimesheetData, extraHoursRate, paydayAdditionsData, paydayDeductionsData, paydayStrikesData, plInsuranceRate, paydayPlInsuranceSkillData?.data) : null;

  const isFlatRate = useMemo(() => {
    return currentCrewMember?.crew_levels?.[0]?.level?.can_provide_feedback === true;
  }, [currentCrewMember?.crew_levels]);

  const mappedJobDates = useMemo(() => {
    if (!jobDatesData?.data?.length) return [];
    return mapJobDateCrewListForFrontend(jobDatesData.data, currentCrewMember?.id || 0, feedbackData?.data || [], { isFlatRate });
  }, [jobDatesData, currentCrewMember?.id, isFlatRate, feedbackData]);

  // Find jobs that need feedback
  const jobsNeedingFeedback = useMemo(() => {
    if (!mappedJobDates.length || !jobDatesData?.data || !feedbackData?.data || !currentCrewMember?.id) return null;

    const now = new Date();
    const feedbackHours = isFlatRate ? 48 : 24;

    // Find the first job that needs feedback
    for (const jobDateGroup of mappedJobDates) {
      for (const jobDate of jobDateGroup.data) {
        const canProvideFeedback = jobDate.is_authed_crew_chief || jobDate.is_flat_rate;

        if (!canProvideFeedback) {
          continue;
        }

        // Get all crew members for this job date (excluding current user)
        const otherCrewMembers = feedbackData.data.filter(
          jc => jc.job_date_id === jobDate.id && jc.crew_id !== currentCrewMember.id
        );

        // Skip if there's only one crewman total (can't feedback yourself)
        if (otherCrewMembers.length === 0) {
          continue;
        }

        // Check if current user has given feedback to all OTHER non-chief crew members
        // (excluding themselves and chiefs, since chiefs don't need feedback)
        const allOtherCrewFeedbackComplete = otherCrewMembers.every((jc) => {
          // Chiefs don't need feedback
          if (jc.is_chief) {
            return true;
          }
          // Check if current user has given feedback to this crew member
          return !!jc.job_crew_feedback?.find((feedback) => feedback.author_id === currentCrewMember.id);
        });

        // Skip if all feedback is complete for other crew members
        if (allOtherCrewFeedbackComplete) {
          continue;
        }

        const needsFeedback = jobDate.is_past_date &&
          !jobDate.feedback_time_past &&
          !jobDate.is_cancelled;

        if (needsFeedback) {
          // Find the original job date data to calculate deadline
          const originalJobDate = jobDatesData.data.find(jd => jd.date_id === jobDate.id);
          if (originalJobDate && originalJobDate.date_started_at) {
            const startDate = new Date(originalJobDate.date_started_at);
            const shiftEndDate = startDate && originalJobDate.date_hours_needed
              ? addHours(startDate, originalJobDate.date_hours_needed)
              : null;

            if (shiftEndDate) {
              const feedbackDeadline = addHours(shiftEndDate, feedbackHours);
              const timeRemaining = feedbackDeadline.getTime() - now.getTime();

              if (timeRemaining > 0) {
                return {
                  jobDate,
                  deadline: feedbackDeadline,
                  timeRemaining,
                  date: startDate,
                };
              }
            }
          }
        }
      }
    }
    return null;
  }, [mappedJobDates, jobDatesData, isFlatRate, feedbackData, currentCrewMember?.id]);

  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!jobsNeedingFeedback) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const remaining = jobsNeedingFeedback.deadline.getTime() - now;
      setTimeRemaining(Math.max(0, remaining));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [jobsNeedingFeedback]);

  // Format countdown
  const formatCountdown = useCallback((ms: number): string => {
    if (ms <= 0) return '0h 0m';

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  }, []);

  // Filter job dates based on selected date
  const filteredJobDates = useMemo(() => {
    if (!selectedDate) return mappedJobDates; // Show all if no date selected
    return mappedJobDates.filter(section => section.title === selectedDate);
  }, [mappedJobDates, selectedDate]);

  const mappedCrewAvailability = useMemo(() => {
    if (!crewAvailability) return [];
    return crewAvailability;
  }, [crewAvailability]);

  const marked = useMemo(() => {
    return getMarkedDates(mappedJobDates, mappedCrewAvailability, selectedDate || undefined, paydayDates);
  }, [mappedJobDates, mappedCrewAvailability, selectedDate, paydayDates]);

  // Use the first item in the sorted history array (most recent) instead of find
  const latestContractType = contractTypeHistory && contractTypeHistory.length > 0
    ? contractTypeHistory[0].type_id
    : currentCrewMember?.contract_type;
  const isEmployee = latestContractType === 1;


  // Ensure we refetch data when the component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([jobDatesRefetch(), crewAvailabilityRefetch(), timesheetRefetch(), additionsRefetch(), deductionsRefetch(), strikesRefetch(), plInsuranceSkillRefetch(), feedbackRefetch()]);
      setInitialLoad(false);
    };

    fetchInitialData();
  }, []);

  // Set payment dates on crew data load
  useEffect(() => {
    if (!isEmployee) {
      const now = new Date();
      setPaydayDates(getPaymentDates(now.getMonth() + 1, now.getFullYear(), currentCrewMember?.payroll_preference === "BIWEEKLY"))
    } else {
      // Clear payday dates for employees
      setPaydayDates([]);
    }
  }, [currentCrewMember, isEmployee])

  const renderItem = useCallback(({ item }: any) => {
    return <JobDateItem item={item} />;
  }, []);

  // Read straight off the selected day. Holding the ids in state meant every refetch — which
  // happens on each month change — re-seeded them from *today*, so the card underneath the
  // calendar drifted back to today's period while you were looking at another month.
  const selectedAvailabilities = useMemo(
    () => availabilitiesOnDay(mappedCrewAvailability, selectedDate || today),
    [mappedCrewAvailability, selectedDate, today],
  );

  const onAvailabilityPress = useCallback((availability: CrewAvailability[0]) => {
    // Approved employee availability is the office's to change.
    if (isEmployee && availability.approved === 1) {
      setOpenEmployeeAvailability(true);
      return;
    }

    // A period that has already started opens too. It cannot be edited there — the edit screen
    // says so — but it can be deleted, which used to be impossible from the app.
    router.push({ pathname: '/(app)/home/edit', params: { id: availability.id.toString() } });
  }, [isEmployee, router]);

  const onDayLongPress = useCallback((day: string) => {
    const firstId = marked[day]?.availabilityIds?.[0];
    if (firstId) {
      router.push({ pathname: '/(app)/home/edit', params: { id: firstId.toString() } });
    }
  }, [marked, router]);

  const onDayPress = useCallback((day: string) => {
    setSelectedDate(day);
  }, []);

  const refetch = useCallback(() => {
    jobDatesRefetch();
    crewAvailabilityRefetch();
    timesheetRefetch();
    additionsRefetch();
    deductionsRefetch();
    strikesRefetch();
    plInsuranceSkillRefetch();
    feedbackRefetch();
  }, [jobDatesRefetch, crewAvailabilityRefetch, timesheetRefetch, additionsRefetch, deductionsRefetch, strikesRefetch, plInsuranceSkillRefetch, feedbackRefetch]);

  useRefetchOnFocus(refetch);


  // Calculate total hours and pay
  const totalHours = mappedTimesheet?.totalHours || 0;
  const totalPay = Math.abs(mappedTimesheet?.total || 0).toFixed(2);

  // Calculate daily hours and pay when calendar is hidden
  const dailyStats = useMemo(() => {
    if (!mappedTimesheet || !selectedDate || showCalendar) return null;

    const selectedDateFormatted = formatDate(new Date(selectedDate), 'dd/MM/yyyy');
    let dailyHours = 0;
    let dailyPay = 0;

    mappedTimesheet.rows.forEach(row => {
      if (row.date === selectedDateFormatted) {
        dailyHours += (row.hours || 0) + (row.crew_hours || 0);
        dailyPay += row.rowTotal || 0;
      }
    });

    return {
      hours: dailyHours,
      pay: Math.abs(dailyPay).toFixed(2)
    };
  }, [mappedTimesheet, selectedDate, showCalendar]);

  const paydayHours = mappedPaydayTimesheet?.totalHours || 0;
  const paydayPay = Math.abs(mappedPaydayTimesheet?.total || 0).toFixed(2);

  const onMonthChange = useCallback((month: DateData) => {
    // Update selectedDate to the first day of the new month
    setSelectedDate(month.dateString);

    !isEmployee && setPaydayDates(getPaymentDates(month.month, month.year, currentCrewMember?.payroll_preference === "BIWEEKLY"));
  }, []);


  // Navigation functions for date arrows
  const navigateToPreviousDay = useCallback(() => {
    setSelectedDate(addDaysToDayKey(selectedDate || today, -1));
  }, [selectedDate, today]);

  const navigateToNextDay = useCallback(() => {
    setSelectedDate(addDaysToDayKey(selectedDate || today, 1));
  }, [selectedDate, today]);

  // Show full UI always (with loading indicators as needed) instead of conditional rendering
  return (
    <View style={styles.container}>
      {/* Feedback Banner */}
      {jobsNeedingFeedback && timeRemaining !== null && timeRemaining > 0 && (
        <TouchableOpacity
          onPress={() => router.push(`/home/${jobsNeedingFeedback.jobDate.id}`)}
          style={styles.feedbackBanner}
        >
          <View style={styles.feedbackBannerContent}>
            <Ionicons name="alert-circle" size={20} color="#FF6B6B" />
            <View style={styles.feedbackBannerText}>
              <Text style={styles.feedbackBannerTitle}>
                Feedback Required - {jobsNeedingFeedback.jobDate.client_name}
              </Text>
              <Text style={styles.feedbackBannerSubtitle}>
                {formatDate(jobsNeedingFeedback.date, 'dd/MM/yyyy')} • {formatCountdown(timeRemaining)} remaining
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#FF6B6B" />
          </View>
        </TouchableOpacity>
      )}
      <View style={[styles.calendarContainer, !showCalendar && styles.calendarContainerHidden]}>
        <CalendarProvider
          date={today}
          theme={todayBtnTheme}
          onDateChanged={setSelectedDate}
        >
          {showCalendar && (
            weekView ? (
              <WeekCalendar
                firstDay={1}
                markedDates={marked}
                onDayPress={(day) => onDayPress(day.dateString)}
              />
            ) : (
              <Calendar
                theme={theme}
                firstDay={1}
                monthFormat={'MMMM yyyy'}
                markedDates={marked}
                disabledByDefault={false}
                disableAllTouchEventsForDisabledDays
                hideExtraDays={true}
                markingType='period'
                onDayLongPress={(day: any) => onDayLongPress(day.dateString)}
                onDayPress={(day: any) => onDayPress(day.dateString)}
                onMonthChange={onMonthChange}
                dayComponent={({ date, state, marking }: DayComponentProps) => {
                  if (!date) return null;
                  return (
                  <Button
                    variant="ghost"
                    className={cn("w-full p-0 m-0 flex flex-col", marking?.color && "rounded-none", marking?.startingDay && "rounded-l-full", marking?.endingDay && "rounded-r-full")}
                    style={(marking?.color ? { backgroundColor: marking?.color } : {})}
                    onPress={() => onDayPress(date.dateString)}
                    onLongPress={() => onDayLongPress(date.dateString)}
                  >
                    <Text className={cn(state === "today" && "font-bold", marking?.selected && "font-extrabold", marking?.color && "text-white")}>
                      {date.day}
                    </Text>
                    <View className="w-full h-4 flex flex-row items-center justify-center gap-1">
                      {(() => {
                        const maxTotalDots = 3;
                        const isPayDay = marking?.payDay;
                        const jobCount = marking?.jobs ?? 0;

                        let dotsToShow = jobCount;
                        if (isPayDay) {
                          dotsToShow = Math.min(jobCount, maxTotalDots - 1);
                        } else {
                          dotsToShow = Math.min(jobCount, maxTotalDots);
                        }

                        const extra = jobCount - dotsToShow;

                        return (
                          <>
                            {isPayDay && (
                              <View className="text-xs text-white rounded-full bg-blue-500 aspect-square w-2 h-2" />
                            )}
                            {Array.from({ length: dotsToShow }).map((_, idx) => (
                              <View
                                key={idx}
                                className="text-xs text-white rounded-full aspect-square w-2 h-2"
                                style={marking?.dotColor ? { backgroundColor: marking.dotColor } : {}}
                              />
                            ))}
                            {extra > 0 && (
                              <Text className="text-xs text-orange-500 font-bold">+{extra}</Text>
                            )}
                          </>
                        );
                      })()}
                    </View>
                  </Button>
                  );
                }}
              />
            )
          )}
          {showCalendar && selectedAvailabilities.length > 0 && (
            <View>
              {selectedAvailabilities.map(availability => (
                <AvailabilityCard
                  key={availability.id}
                  availability={availability}
                  onPress={onAvailabilityPress}
                />
              ))}
            </View>
          )}

          <View className="bg-white py-3 px-6 border-y border-gray-200 flex-row justify-between items-center">
            {!showCalendar && selectedDate && (
              <Pressable onPress={navigateToPreviousDay} className="p-2">
                <Ionicons name="chevron-back" size={24} color={colors.primary} />
              </Pressable>
            )}
            <View style={{ flex: 1 }}>
              {selectedDate ? (
                <Text className="text-lg font-semibold text-center text-black">
                  {formatDate(new Date(selectedDate), 'EEEE, MMMM d, yyyy')}
                </Text>
              ) : (
                <Text className="text-lg text-center font-semibold text-black">All Jobs</Text>
              )}
            </View>
            {!showCalendar && selectedDate && (
              <Pressable onPress={navigateToNextDay} className="p-2">
                <Ionicons name="chevron-forward" size={24} color={colors.primary} />
              </Pressable>
            )}
          </View>

          {paydayDates?.some(payday => format(payday, 'yyyy-MM-dd') === selectedDate) ? (
            <View className="relative flex flex-row items-center justify-between bg-blue-500 px-4 py-2 rounded-full mx-6 my-2">
              <Ionicons name="wallet-outline" size={24} color="white" />
              <Text className="absolute left-1/2 -translate-x-1/2 ml-4 text-white font-semibold">
                Pay Day
              </Text>
              {/* <Text className="text-white font-semibold">
                (£{paydayPay})
              </Text> */}
            </View>
          ) : null}

          {!showCalendar && selectedAvailabilities.length > 0 && (
            <View>
              {selectedAvailabilities.map(availability => (
                <AvailabilityCard
                  key={availability.id}
                  availability={availability}
                  onPress={onAvailabilityPress}
                />
              ))}
            </View>
          )}

          <View style={[styles.agendaContainer, !showCalendar && styles.agendaFullHeight]}>
            {isLoading || crewAvailabilityLoading ? (
              // Show loading indicator within the agenda area
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading schedule...</Text>
              </View>
            ) : !mappedJobDates.length ? (
              <View style={styles.emptyState}>
                <Ionicons name="calendar-outline" size={60} strokeWidth={0.5} color="#CCCCCC" />
                <Text className="text-gray-500 text-lg mt-4">No jobs scheduled</Text>
              </View>
            ) : selectedDate && filteredJobDates.length === 0 ? (
              <View style={styles.emptyState}>
                <Ionicons name="calendar-outline" size={60} strokeWidth={0.5} color="#CCCCCC" />
                <Text className="text-gray-500 text-lg mt-4">No jobs scheduled for this day</Text>
                <Text className="text-gray-500 text-sm mt-2">Select a different date from the calendar</Text>
              </View>
            ) : (
              <AgendaList
                sections={filteredJobDates}
                renderItem={renderItem}
                renderSectionHeader={renderSectionHeader}
                sectionStyle={{ backgroundColor: '#f5f5f5' }}
                style={styles.agenda}
                contentContainerStyle={styles.agendaContent}
                dayFormat={'dd MMM yyyy'}
                scrollToNextEvent
                markToday
                refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
              />
            )}
          </View>
        </CalendarProvider>
      </View>

      {/* Bottom totals bar - always shown */}
      <View style={[styles.totalBar, { paddingBottom: Platform.OS === 'ios' ? 20 : insets.bottom }]}>
        <View style={styles.totalContent}>
          <View>
            <Text style={styles.totalLabel}>{!showCalendar && selectedDate ? 'Hours this day' : 'Hours this month'}</Text>
            <Text style={styles.totalValue}>{!showCalendar && dailyStats ? dailyStats.hours : totalHours}</Text>
          </View>
          <View>
            <Text style={styles.totalLabel}>{!showCalendar && selectedDate ? 'Pay this day' : 'Pay this month'}</Text>
            <Text style={styles.totalValue}>£{!showCalendar && dailyStats ? dailyStats.pay : totalPay}</Text>
          </View>
        </View>
      </View>

      <AvailabilityPastAlert open={openEmployeeAvailability} onOpenChange={setOpenEmployeeAvailability} message="You cannot edit approved availability" />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  feedbackBanner: {
    backgroundColor: '#FFF5F5',
    borderBottomWidth: 1,
    borderBottomColor: '#FF6B6B',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  feedbackBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  feedbackBannerText: {
    flex: 1,
  },
  feedbackBannerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FF6B6B',
    marginBottom: 2,
  },
  feedbackBannerSubtitle: {
    fontSize: 12,
    color: '#FF6B6B',
    opacity: 0.8,
  },
  calendarContainer: {
    flex: 1,
    marginBottom: Platform.OS === 'ios' ? 90 : 70, // Ensures space for total bar at bottom
  },
  calendarContainerHidden: {
    marginBottom: 0,
  },
  agendaContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  agendaFullHeight: {
    marginBottom: Platform.OS === 'ios' ? 90 : 70, // Ensures space for total bar at bottom
  },
  agenda: {
    backgroundColor: '#f5f5f5',
  },
  agendaContent: {
    paddingBottom: 100, // Extra space at bottom to ensure last item is visible
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 60, // Space for bottom bar
    paddingTop: 20, // Space for pay day banner
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 100, // Space for bottom bar
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#6b7280',
  },
  totalBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#e5e5e5',
    // paddingBottom is applied inline: Android needs the live safe-area inset
    // because targetSdk 36 forces edge-to-edge and the gesture bar overlaps.
    zIndex: 1000, // Ensure it's always on top
  },
  totalContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 4,
  },
  totalLabel: {
    fontSize: 12,
    color: '#6b7280',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000',
    marginTop: 4,
  }
});

export default React.memo(ExpandableCalendarScreen);
